import { IntlMessageFormat } from 'intl-messageformat';
import { TranslatedStringTable, TranslatedString } from '@fluidx/localize-js-types';

function getStringPropertyImplementation(translatedString: TranslatedString, locale: string) {
  // We have two types of strings that we support:
  // 1. Strings with no placeholders - we don't need to parse these and can provide them as a string property for easy access.
  // 2. Strings with placeholder inputs - these need to be parsed and turned into a function that can be invoked with the corresponding placeholder values
  switch (translatedString.f) {
    case 0: {
      return translatedString.s;
    }
    case 1: {
      // This implementation parses the AST for the ICU string on every function call, which optimizes for memory consumption at the expense of runtime performance.
      // As we start to use the library in more production scenarios, we could start to memoize the 'IntlMessageFormat' objects to avoid parsing the AST repeatedly if the runtime performance penalty is too high.
      return (placeholders?: Record<string, string | number>) => new IntlMessageFormat(translatedString.s, locale).format(placeholders);
    }
  }
}

/**
 * Generates an object that can be used to generate user facing strings from an ICU string table
 * @param stringTableJson - A JavaScript object mapping unique keys to translated ICU strings
 * @param locale - The locale of the strings in the table
 */
export function getStringProvider<ST>(stringTableJson: TranslatedStringTable, locale: string): ST {
  const entries = Object.entries(stringTableJson.strings);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};

  entries.forEach(([key, value]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result[key] = getStringPropertyImplementation(value, locale);
  });

  return result;
}
